import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import GradientSpinnerRing from "../common/GradientSpinnerRing";
import { createCryptoDepositAddress } from "../../../redux/actions/CryptoTransaction/StoreDepositAddress";

const RING_SIZE = 100;

const TOKEN_ICON_SIZES = {
  sm: { container: "size-6", image: "size-6", text: "text-[10px]" },
  md: { container: "size-14", image: "size-10", text: "text-sm" },
  lg: { container: "size-20", image: "size-16", text: "text-lg" },
};

const TokenIcon = ({ asset, size = "md" }) => {
  const { container, image, text } =
    TOKEN_ICON_SIZES[size] ?? TOKEN_ICON_SIZES.md;

  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full ${container}`}
      style={asset?.iconBg ? { backgroundColor: asset.iconBg } : undefined}
    >
      {asset?.logo ? (
        <img
          src={asset.logo}
          alt={asset?.currency_code ?? "Token"}
          className={`rounded-full object-cover ${image}`}
        />
      ) : (
        <span className={`font-semibold text-text-primary ${text}`}>
          {asset?.currency_code ?? "—"}
        </span>
      )}
    </div>
  );
};

const CreateNewWalletStep = ({
  selectedAsset,
  selectedNetwork,
  onComplete,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const spinnerGradientId = useId();

  // Guard to prevent duplicate API execution on component mount / React StrictMode
  const hasCalledCreateRef = useRef(false);

  const [loading, setLoading] = useState(true);
  // const [walletAddress, setWalletAddress] = useState("");
  const [error, setError] = useState("");

  const symbol = selectedAsset?.currency_code ?? "Asset";
  const networkLabel = selectedNetwork?.name ?? "selected network";
  const networkId = selectedNetwork?.network_id ?? selectedNetwork?.id;

  const handleCreateWallet = useCallback(async () => {
    if (!selectedAsset?.id || !networkId) {
      // setWalletAddress("");
      setError("Invalid asset or network selected.");
      setLoading(false);
      return;
    }

    const payload = {
      account_id: selectedAsset.id,
      network: networkId,
    };

    setLoading(true);
    // setWalletAddress("");
    setError("");

    try {
      const response = await dispatch(createCryptoDepositAddress(payload));

      if (response?.success) {
        // const address = response?.data?.address;

        // setWalletAddress(address || "");
        onComplete();
      } else {
        throw new Error(
          response?.message ||
            "Unable to create a wallet address. Please try again."
        );
      }
    } catch (err) {
      console.error("Wallet creation failed:", err);
      setError(
        err?.message ||
          "Something went wrong while creating your wallet. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedAsset?.id, networkId, onComplete]);

  useEffect(() => {
    if (hasCalledCreateRef.current) return;
    hasCalledCreateRef.current = true;

    handleCreateWallet();
  }, [handleCreateWallet]);

  const handleRetry = () => {
    hasCalledCreateRef.current = false;
    handleCreateWallet();
  };

  // const formattedAddress = useMemo(() => {
  //   if (!walletAddress || typeof walletAddress !== "string") return null;

  //   return {
  //     start: walletAddress.slice(0, 6),
  //     middle: walletAddress.slice(6, -6),
  //     end: walletAddress.slice(-6),
  //   };
  // }, [walletAddress]);

  const isCreating = loading;
  const hasError = !loading && !!error;
  const isSuccess = !loading && !error;

  return (
    <div className="flex h-full min-h-0 w-full flex-col px-6 pt-2 pb-4 max-sm:pb-6">
      <h3 className="shrink-0 text-center text-lg font-semibold leading-7 text-text-primary">
        {isCreating
          ? "Creating New Wallet"
          : hasError
          ? "Wallet Creation Failed"
          : "New Wallet Created"}
      </h3>

      {/* Loading State */}
      {(isCreating || isSuccess) && (
        <>
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 py-4">
            <div
              className="relative flex items-center justify-center"
              style={{ width: RING_SIZE, height: RING_SIZE }}
            >
              <GradientSpinnerRing gradientId={spinnerGradientId} />

              <div className="absolute">
                <TokenIcon asset={selectedAsset} size="lg" />
              </div>
            </div>

            <p className="max-w-[437px] px-2 text-center text-base font-outfit leading-8 text-text-secondary">
              Generating your <strong>{symbol}</strong> wallet on the{" "}
              <strong>{networkLabel}</strong> network for the first time.
            </p>
          </div>

          <button
            type="button"
            disabled
            className="flex h-12 w-full items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
          </button>
        </>
      )}

      {/* Error State */}
      {hasError && (
        <>
          <div className="flex flex-1 flex-col items-center justify-center gap-5">
            <div className="flex size-20 items-center justify-center rounded-full bg-red-100">
              <span className="text-3xl text-red-500">✕</span>
            </div>

            <div className="space-y-2">
              <p className="text-center text-base font-medium text-text-primary">
                Failed to Create Wallet
              </p>

              <p className="max-w-sm text-center text-sm text-text-secondary">
                {error}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleRetry}
              className="flex h-12 w-full items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold text-white hover:bg-[#3fa82c]"
            >
              Try Again
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="flex h-12 w-full items-center justify-center rounded-[10px] border border-border-primary text-sm font-semibold text-text-primary"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateNewWalletStep;